import * as React from "react";
import * as moment from "moment";

import { OhCase } from "../model";
import { AnsweredQuestionWithId, DropdownOption, QuestionnaireData } from "../questionnaires/model";
import { TaskModel } from "../task/taskActions/models";

import { QuestionnaireA as QuestionnaireAForm } from "./components/QuestionnaireA";
import { QuestionnaireB as QuestionnaireBForm } from "./components/QuestionnaireB";

export enum ManagerSatisfactionFollowUpQuestionnaireType {
    QuestionnaireA = 1,
    QuestionnaireB = 2
}

export const getQuestionnaireForm = (questionnaireType: number, props: QuestionnaireFormProps): JSX.Element => {
    switch (questionnaireType) {
        case ManagerSatisfactionFollowUpQuestionnaireType.QuestionnaireA:
            return <QuestionnaireAForm { ...props } />;
        case ManagerSatisfactionFollowUpQuestionnaireType.QuestionnaireB:
            return <QuestionnaireBForm { ...props } />;
        default:
            return <h2>Improper questionnaire type </h2>;
    }
};

export interface ManagerSatisfactionFollowUpQuestionnaireModel {
    questions: AnsweredQuestionWithId[];
    eTag: string;
}

export interface ManagerSatisfactionFollowUpQuestionnaireRequestDto {
    questionnaireType: ManagerSatisfactionFollowUpQuestionnaireType;
    reportCompletedDate: moment.Moment;
    viewReportTaskId: string;
    questionnaireData: QuestionnaireData;
}

export const MANAGER_SATISFACTION_FOLLOW_UP_QUESTIONNAIRE = "MANAGER_SATISFACTION_FOLLOW_UP_QUESTIONNAIRE";

export const DATE_FORMAT = "DD-MM-YYYY";

enum WhichOfTheFollowingBestDescribesTheIndividualsCurrentSituation {
    AtWorkWithPermanentAdjustmentsInPlace = "AtWorkWithPermanentAdjustmentsInPlace",
    AtWorkWithTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork = "AtWorkWithTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork",
    AtWorkWithNoAdjustmentsInPlace = "AtWorkWithNoAdjustmentsInPlace"
}

export const whichOfTheFollowingBestDescribesTheIndividualsCurrentSituationOptions: DropdownOption[] = [
    {
        text: "At Work with permanent adjustments in place",
        value: WhichOfTheFollowingBestDescribesTheIndividualsCurrentSituation.AtWorkWithPermanentAdjustmentsInPlace,
        order: 1
    },
    {
        text: "At Work with temporary adjustments in place (including any phased return to work)",
        value: WhichOfTheFollowingBestDescribesTheIndividualsCurrentSituation.AtWorkWithTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork,
        order: 2
    },
    {
        text: "At Work with no adjustments in place",
        value: WhichOfTheFollowingBestDescribesTheIndividualsCurrentSituation.AtWorkWithNoAdjustmentsInPlace,
        order: 3
    }
];

enum WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork {
    TheEmployeesMedicalConditionHasDeterioratedOrChanged = "TheEmployeesMedicalConditionHasDeterioratedOrChanged",
    TheEmployeeIsOffWorkWithADifferentMedicalCondition = "TheEmployeeIsOffWorkWithADifferentMedicalCondition",
    TheGPAdvisedThemToNotGoToWork = "TheGPAdvisedThemToNotGoToWork",
    TheEmployeeWasUnwillingToComeToWork = "TheEmployeeWasUnwillingToComeToWork",
    TheEmployeeIsOffWorkDueToPlannedSurgeyOrMedicalIntervention = "TheEmployeeIsOffWorkDueToPlannedSurgeyOrMedicalIntervention",
    DoNotKnow = "DoNotKnow"
}

export const whichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWorkOptions: DropdownOption[] = [
    {
        text: "The employee’s medical condition has deteriorated or changed since the last referral to OH",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.TheEmployeesMedicalConditionHasDeterioratedOrChanged,
        order: 1
    },
    {
        text: "The employee is off work with a different medical condition",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.TheEmployeeIsOffWorkWithADifferentMedicalCondition,
        order: 2
    },
    {
        text: "The GP advised them to not go to work",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.TheGPAdvisedThemToNotGoToWork,
        order: 3
    },
    {
        text: "The employee was unwilling to come to work",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.TheEmployeeWasUnwillingToComeToWork,
        order: 4
    },
    {
        text: "The employee is off work due to planned surgery or medical intervention and is expected back to work",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.TheEmployeeIsOffWorkDueToPlannedSurgeyOrMedicalIntervention,
        order: 5
    },
    {
        text: "Don’t know",
        value: WhichOfTheBelowBestDescribesWhyTheEmployeeIsNotAtWork.DoNotKnow,
        order: 6
    }
];

enum WhenDidTheyReturnToWork {
    BeforeTheOHAdvisedReturnToWorkDate = "BeforeTheOHAdvisedReturnToWorkDate",
    OnOrNearTheOHAdvisedReturnToWorkDate = "OnOrNearTheOHAdvisedReturnToWorkDate",
    AfterTheOHAdvisedReturnToWorkDate = "AfterTheOHAdvisedReturnToWorkDate",
    DoNotKnow = "DoNotKnow"
}

export const whenDidTheyReturnToWorkOptions: DropdownOption[] = [
    {
        text: "Before the OH advised return to work date",
        value: WhenDidTheyReturnToWork.BeforeTheOHAdvisedReturnToWorkDate,
        order: 1
    },
    {
        text: "On or near the OH advised return to work date",
        value: WhenDidTheyReturnToWork.OnOrNearTheOHAdvisedReturnToWorkDate,
        order: 2
    },
    {
        text: "After the OH advised return to work date",
        value: WhenDidTheyReturnToWork.AfterTheOHAdvisedReturnToWorkDate,
        order: 3
    },
    {
        text: "Don’t know",
        value: WhenDidTheyReturnToWork.DoNotKnow,
        order: 4
    }
];

enum WhichOfTheFollowingBestDescribesTheCurrentWorkSituation {
    AtWorkWithPermanentAdjustmentsInPlace = "AtWorkWithPermanentAdjustmentsInPlace",
    AtWorkWithAgreedTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork = "AtWorkWithAgreedTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork",
    // eslint-disable-next-line max-len
    AtWorkWithTemporaryAdjustmentsInPlaceThatHaveContinuedForLongerThanOriginallyPlaned = "AtWorkWithTemporaryAdjustmentsInPlaceThatHaveContinuedForLongerThanOriginallyPlaned",
    AtWorkWithNoAdjustmentsInPlace = "AtWorkWithNoAdjustmentsInPlace"
}

export const whichOfTheFollowingBestDescribesTheCurrentWorkSituationOptions: DropdownOption[] = [
    {
        text: "At Work with permanent adjustments in place",
        value: WhichOfTheFollowingBestDescribesTheCurrentWorkSituation.AtWorkWithPermanentAdjustmentsInPlace,
        order: 1
    },
    {
        text: "At Work with agreed temporary adjustments in place (including any phased return to work)",
        value: WhichOfTheFollowingBestDescribesTheCurrentWorkSituation.AtWorkWithAgreedTemporaryAdjustmentsInPlaceIncludingAnyPhasedReturnToWork,
        order: 2
    },
    {
        text: "At Work with temporary adjustments in place that have continued for longer than originally planned",
        value: WhichOfTheFollowingBestDescribesTheCurrentWorkSituation.AtWorkWithTemporaryAdjustmentsInPlaceThatHaveContinuedForLongerThanOriginallyPlaned,
        order: 3
    },
    {
        text: "At Work with no adjustments in place",
        value: WhichOfTheFollowingBestDescribesTheCurrentWorkSituation.AtWorkWithNoAdjustmentsInPlace,
        order: 4
    }
];

enum WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture {
    ExpectTheIndividualToReturnToWorkWithinTheNextWeek = "ExpectTheIndividualToReturnToWorkWithinTheNextWeek",
    ExpectTheIndividualToReturnToWorkWithinTheNextTwoThreeWeeks = "ExpectTheIndividualToReturnToWorkWithinTheNextTwoThreeWeeks",
    DoNotExpectThemReturnToWorkFormMoreThanThreeWeeks = "DoNotExpectThemReturnToWorkFormMoreThanThreeWeeks",
    DoNotKnowWhenTheyWillReturnToWork = "DoNotKnowWhenTheyWillReturnToWork"
}

export const whichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFutureOptions: DropdownOption[] = [
    {
        text: "I expect the individual to return to work within the next week",
        value: WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture.ExpectTheIndividualToReturnToWorkWithinTheNextWeek,
        order: 1
    },
    {
        text: "I expect the individual to return to work within the next 2-3 weeks",
        value: WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture.ExpectTheIndividualToReturnToWorkWithinTheNextTwoThreeWeeks,
        order: 2
    },
    {
        text: "I do not expect them return to work for more than 3 weeks",
        value: WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture.DoNotExpectThemReturnToWorkFormMoreThanThreeWeeks,
        order: 3
    },
    {
        text: "I do not know when they will return to work",
        value: WhichOfTheFollowingBestDescribesTheSituationWithRegardsToReturnToWorkInTheFuture.DoNotKnowWhenTheyWillReturnToWork,
        order: 4
    }
];

enum WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork {
    UnableToMeetWithThemAndAgreeReturnToWorkDate = "UnableToMeetWithThemAndAgreeReturnToWorkDate",
    TheIndividualWasUnableToMeetWithMe = "TheIndividualWasUnableToMeetWithMe",
    TheAdjustmentsRecommendedInTheWorkplaceWereNotPractical = "TheAdjustmentsRecommendedInTheWorkplaceWereNotPractical",
    TheGPAdvisedDifferentReturnToWorkDate = "TheGPAdvisedDifferentReturnToWorkDate",
    TheIndividualWasUnwillingToReturnToWork = "TheIndividualWasUnwillingToReturnToWork",
    TheIndividualHasLeftOrIsLeavingTheirJob = "TheIndividualHasLeftOrIsLeavingTheirJob"
}

export const whichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWorkOptions: DropdownOption[] = [
    {
        text: "I was unable to meet with them and agree a return to work date",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.UnableToMeetWithThemAndAgreeReturnToWorkDate,
        order: 1
    },
    {
        text: "The individual was unable to meet with me",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.TheIndividualWasUnableToMeetWithMe,
        order: 2
    },
    {
        text: "The adjustments recommended in the workplace were not practical",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.TheAdjustmentsRecommendedInTheWorkplaceWereNotPractical,
        order: 3
    },
    {
        text: "The GP advised a different return to work date",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.TheGPAdvisedDifferentReturnToWorkDate,
        order: 4
    },
    {
        text: "The individual was unwilling to return to work",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.TheIndividualWasUnwillingToReturnToWork,
        order: 5
    },
    {
        text: "The individual has left or is leaving their job",
        value: WhichOfTheFollowingBestDescribesWhyTheEmployeeHasNotReturnedToWork.TheIndividualHasLeftOrIsLeavingTheirJob,
        order: 6
    }
];

export const parseQuestionnaireRequestModel = (model: ManagerSatisfactionFollowUpQuestionnaireRequestDto) => {
    return {
        ...model,
        reportCompletedDate: model.reportCompletedDate && moment(model.reportCompletedDate)
    };
};

export interface QuestionnaireFormProps {
    ohCase: OhCase;
    task: TaskModel;
    loadQuestionnaire?: (customerId: string, ohCaseId: string, taskId: string) => Promise<void>;
    submitQuestionnaire?: (questions: AnsweredQuestionWithId[]) => Promise<boolean>;
    questions: AnsweredQuestionWithId[];
}
